<!-- The trace. This is the product; everything else on Now is a caption on it.

     Two variables live in ONE shape. The polyline is probability. The area
     beneath it is volume, drawn as eleven separate trapezoids rather than one
     polygon, each taking `rain` at an alpha computed from THAT interval's
     millimetres. A high-chance drizzle is a tall pale hump; a high-chance
     downpour is a tall saturated one. A single flat alpha across the whole area
     loses the distinction and there is no legend to put it back — so do not
     simplify this to one <polygon>.

     THE PAPER NEVER GOES BLANK. Grid, baseline and hour labels are drawn before
     anything is known and stay drawn when nothing is. An empty pluviograph is
     still a pluviograph; a blank rectangle is a bug someone has to report. -->
<template>
  <div class="rain-trace" :class="{ 'is-stale': isStale }">
    <svg viewBox="0 0 320 158" role="img" :aria-label="summary">
      <rect class="paper" x="0" y="0" width="320" height="158" />

      <rect v-if="isDry" class="dry-wash" x="12" y="30" width="288" height="100" />

      <g class="grid">
        <line
          v-for="(x, i) in slotX" :key="'v' + i"
          :x1="x" y1="24" :x2="x" y2="130"
          :class="{ major: i % 3 === 0 }"
        />
        <line
          v-for="y in [30, 55, 80, 105]" :key="'h' + y"
          x1="12" :y1="y" x2="300" :y2="y"
        />
      </g>
      <line class="baseline" x1="12" y1="130" x2="300" y2="130" />

      <g v-if="hasTrace" class="volume">
        <polygon
          v-for="seg in segments" :key="seg.key"
          :points="seg.points" :fill-opacity="seg.alpha"
        />
      </g>

      <polyline v-if="hasTrace" class="trace" :points="tracePoints" />

      <line class="now-rule" :x1="slotX[0]" y1="20" :x2="slotX[0]" y2="134" />
      <circle v-if="hasTrace" class="now-dot" :cx="slotX[0]" :cy="pointY[0]" r="3.5" />

      <text v-if="peak" class="peak" :x="peak.x" :y="peak.y">{{ peak.label }}</text>

      <text v-if="!hasTrace" class="absent" x="156" y="84" text-anchor="middle">
        No forecast
      </text>

      <g class="hour-labels">
        <text v-for="l in hourLabels" :key="l.key" :x="l.x" y="147">{{ l.text }}</text>
      </g>
    </svg>

    <p v-if="caption" class="caption" :class="{ warn: isStale || !hasTrace }">{{ caption }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  hours: { type: Array, default: () => [] },
  observedAt: { type: String, default: null },
  thresholdMm: { type: Number, default: 0.2 },
  // Passed in rather than read from the clock, so staleness re-evaluates when
  // the app's minute ticks instead of only when something else re-renders.
  now: { type: String, default: null },
});

const SLOTS = 12;
const LEFT = 12;
const SPAN = 288;
const BASE = 130;
const TOP = 30;

const slotX = computed(() => Array.from(
  { length: SLOTS },
  (_, i) => LEFT + (SPAN / (SLOTS - 1)) * i,
));

const known = computed(() => props.hours.slice(0, SLOTS));
const hasTrace = computed(() => known.value.length >= 2);

const mmOf = (h) => (Number.isFinite(Number(h?.mm)) ? Number(h.mm) : 0);

const pointY = computed(() => known.value.map(
  (h) => BASE - Math.max(0, Math.min(100, Number(h.chance) || 0)),
));

const tracePoints = computed(() => known.value
  .map((_, i) => `${slotX.value[i]},${pointY.value[i]}`)
  .join(' '));

const segments = computed(() => known.value.slice(0, -1).map((_, i) => ({
  key: known.value[i].time ?? `seg-${i}`,
  // The whole idea, in one line. Capped at 0.55 so even a deluge stays paper.
  alpha: Math.min(0.55, 0.1 + mmOf(known.value[i + 1]) * 0.09),
  points: [
    `${slotX.value[i]},${pointY.value[i]}`,
    `${slotX.value[i + 1]},${pointY.value[i + 1]}`,
    `${slotX.value[i + 1]},${BASE}`,
    `${slotX.value[i]},${BASE}`,
  ].join(' '),
})));

const totalMm = computed(() => known.value.reduce((sum, h) => sum + mmOf(h), 0));
const isDry = computed(() => hasTrace.value && totalMm.value < props.thresholdMm);

const ageMinutes = computed(() => {
  if (!props.observedAt) return 0;
  const ref = props.now ? new Date(props.now) : new Date();
  return (ref - new Date(props.observedAt)) / 60_000;
});
const isStale = computed(() => ageMinutes.value > 90);

const clock = (iso) => new Date(iso)
  .toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });

const hourLabels = computed(() => known.value.reduce((out, h, i) => {
  // Keyed by index, not by text: two slots can legitimately read the same time
  // across a DST fold, and a duplicate key drops one of them.
  if (i % 3 === 0) out.push({ key: i, x: slotX.value[i] - (i === 0 ? 0 : 8), text: clock(h.time) });
  return out;
}, []));

const peak = computed(() => {
  if (!hasTrace.value || isDry.value) return null;
  let best = 0;
  known.value.forEach((h, i) => { if (mmOf(h) > mmOf(known.value[best])) best = i; });
  if (mmOf(known.value[best]) < props.thresholdMm) return null;
  return {
    x: Math.min(268, Math.max(12, slotX.value[best] - 18)),
    y: Math.max(TOP - 8, pointY.value[best] - 9),
    label: `${mmOf(known.value[best]).toFixed(1)} mm`,
  };
});

// Says what happened and when it was last good. Never "Oops", never blank.
const caption = computed(() => {
  if (!hasTrace.value) {
    return props.observedAt ? `No forecast. Last checked ${clock(props.observedAt)}.` : 'No forecast.';
  }
  if (isStale.value) return `Forecast from ${clock(props.observedAt)}. Refreshing.`;
  if (isDry.value) return `Dry through ${clock(known.value[known.value.length - 1].time)}.`;
  if (known.value.length < SLOTS) return `Forecast runs to ${clock(known.value[known.value.length - 1].time)}.`;
  return null;
});

// Colour carries the volume; this carries it again, in words, for the label.
const summary = computed(() => {
  if (!hasTrace.value) return 'Rain forecast unavailable.';
  if (isDry.value) return `No rain expected in the next ${known.value.length} hours.`;
  const p = known.value.reduce((a, b) => (mmOf(b) > mmOf(a) ? b : a));
  return `Rain chance over the next ${known.value.length} hours, peaking at `
    + `${p.chance} per cent around ${clock(p.time)} with ${mmOf(p).toFixed(1)} millimetres.`;
});
</script>

<style scoped>
.rain-trace {
  --paper: var(--oh-paper, #dfe3d1);
  --grid: var(--oh-grid, #b0522f);
  --ink: var(--oh-ink, #1b2540);
  --rain: var(--oh-rain, #35689f);
  --signal: var(--oh-signal, #a8331a);
  --sun: var(--oh-sun, #e0a62b);
}
svg { display: block; width: 100%; }
.paper { fill: var(--paper); }
.dry-wash { fill: var(--sun); fill-opacity: 0.08; }
.grid line { stroke: var(--grid); stroke-opacity: 0.16; }
.grid line.major { stroke-opacity: 0.26; }
.baseline { stroke: var(--grid); stroke-opacity: 0.4; }
.volume polygon { fill: var(--rain); }
.trace {
  fill: none;
  stroke: var(--ink);
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-dasharray: 620;
  stroke-dashoffset: 620;
  animation: ink 600ms ease-out forwards;
}
.now-rule { stroke: var(--signal); stroke-opacity: 0.75; }
.now-dot { fill: var(--ink); }
text {
  font-family: 'Chivo Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  fill: var(--ink);
}
.hour-labels text { fill-opacity: 0.6; }
.absent { fill-opacity: 0.6; }
.is-stale .trace { opacity: 0.5; animation: none; stroke-dashoffset: 0; }
.caption {
  margin: 6px 14px 0;
  font-family: 'Chivo Mono', ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.caption.warn { color: var(--signal); }
@keyframes ink { to { stroke-dashoffset: 0; } }

/* The trace draw is the only animation in the app, and it is still optional. */
@media (prefers-reduced-motion: reduce) {
  .trace { animation: none; stroke-dashoffset: 0; }
}
</style>
